import { Observable } from 'rxjs';
import { Option, PayrollCampus, Result } from "..";
import { Action, ActionType, Case, CaseAdvancement, CaseReconciliation, HRAcademic, HRCommand, HRUnit, NewsStory, Position, ReconChange, Supervisor, SupervisorDirectReports, TermId, Title } from '../types/ahr';

export class PurePositionId {
	constructor(
		public readonly titleId: number,
		public readonly rankId: number,
		public readonly stepId: number
    ){}
}
export class ProgramReviewSettings {
    constructor(
        public readonly periodOfReviewEndingInYearId: number,
        public readonly resultsVisibleToCandidates: boolean
    ){}

    public static fromJson(o: any): ProgramReviewSettings {
        return new ProgramReviewSettings(
            o.periodOfReviewEndingInYearId,
            o.resultsVisibleToCandidates
        );
    }
}



export class Milestone {
    constructor(
        public readonly key: string,
        public readonly displayName: string,
        public readonly type: string,           // "Open" | "Due"
        public readonly referenceOpenKey: string | null
    ){}

    public static fromJson(o: any): Milestone {
        return new Milestone(
            o.key,
            o.displayName,
            o.type,
            o.referenceOpenKey || null
        );
    }
}

export interface ReviewDateEntry {
    milestoneKey: string;
    date: string | null;   // YYYY-MM-DD on the wire, or null when unset
}

export interface CaseReviewDateOverridesResponse {
    caseId: number;
    milestones: Milestone[];
    dates: ReviewDateEntry[];   // only the milestones overridden for this case
}

export interface SystemClockInfo {
    simulatedDate: string | null;   // YYYY-MM-DD, or null when running on the real clock
    isProduction: boolean;
}

export interface EligibilityListReportRow {
    academicUserName: string;
    academicEmail: string;
    academicTitle: string;
    academicSeparationDate: Date | null;
    eligibleActions: Action[];
    supervisors: Supervisor[];
}

export class ReviewDatesResponse {
    constructor(
        public readonly year: number,
        public readonly milestones: Milestone[],
        public readonly dates: ReviewDateEntry[]
    ){}

    public static fromJson(o: any): ReviewDatesResponse {
        return new ReviewDatesResponse(
            o.year,
            (o.milestones as any[]).map(Milestone.fromJson),
            (o.dates as any[]).map(d => ({ milestoneKey: d.milestoneKey, date: d.date }))
        );
    }
}

export class ReviewDocumentType {
    constructor(
        public readonly id: string,
        public readonly name: string,
        public readonly description: string
    ){}
}

export class ReviewAction {
    constructor(
        public readonly allowed: boolean,
        public readonly action: ActionType,
        public readonly accelerating: boolean, 
        public readonly requirements: {[documentTypeId: string]: CandidateReviewDocument}
    ){}
    public static fromJson(o: any): ReviewAction {
        /* For some reason, JSON.net lower-cases the DU values when serializing to a JSON object.
        ** We have to undo that for easy access. */
        for(var k in o.requirements){
            let K = k[0].toUpperCase() + k.slice(1);
            o.requirements[K] = o.requirements[k];
            delete o.requirements[k];
        }
        return o;
    }
} 

export class CandidateReviewDocument {
    constructor(
        public readonly id: string,
        public readonly name: string,
        public readonly description: string,
        public readonly requirementLabel: string,
        public readonly required: boolean
    ){}
}

export class ComprehensiveReviewRequirements {
    constructor(
        public readonly documentTypes: ReviewDocumentType,
        public readonly actions: ReviewAction[]
    ){ }
    public static fromJson(o: any): ComprehensiveReviewRequirements {
        return new ComprehensiveReviewRequirements(
            o.documentTypes.map(dt => new ReviewDocumentType(dt.id.case, dt.name, dt.description)),
            o.actions.map(ReviewAction.fromJson)
        );
    }
}

export class DossierFileDescriptor {
    public readonly id: string;
    public readonly name: string;
}

export class CaseId {
  constructor(
    readonly academicId: string,
    readonly periodOfReviewEndingInYear: number,
    readonly workflowCaseId: number = 0
  ){}
  public equals(other: CaseId): boolean {
      if (!other) return false;
      // If both have a workflowCaseId, compare by that (unique per case)
      if (this.workflowCaseId > 0 && other.workflowCaseId > 0)
          return this.workflowCaseId === other.workflowCaseId;
      // Fallback for history entries (workflowCaseId = 0)
      return this.academicId === other.academicId
        && this.periodOfReviewEndingInYear === other.periodOfReviewEndingInYear;
  }
  static fromJson(o: any): CaseId {
      return new CaseId(
          o.academicId,
          o.periodOfReviewEndingInYear,
          o.workflowCaseId || 0);
  }
}

export class ReviewDocumentTemplate {
    constructor(
        readonly name: string,
        readonly instructionsHTML: string
    ){}

    static fromJson(o: any): ReviewDocumentTemplate {
        return new ReviewDocumentTemplate(
            o.name,
            o.instructionsHTML);
    }
} 

export class ReviewForm {
    constructor(
        readonly requiredReviewDocument: Option<ReviewDocumentTemplate>
    ){ }

    static fromJson(o: any): ReviewForm {
        return new ReviewForm(
            Option.fromJsonFSharp(o.requiredReviewDocument, ReviewDocumentTemplate.fromJson));
    }
}



export class ReviewerCase {
    get academicId(){ 
        return this.id.academicId;
    }
    get roles(): string {
        return this.yourSteps.map((s: ReviewerStep) => s.title).join(', ');
    }
    constructor(
        readonly id: CaseId,
        readonly caseHistory: CaseId[],
        readonly userId: number,
        readonly separationDate: Date,
        readonly candidateName: string,
        readonly currentPosition: Position,
        readonly proposedAction: Action,
        readonly proposedActionName: string,
        readonly periodOfReview: Option<PeriodOfReview>,
        readonly progress: CaseProgress,
        readonly canViewReviewerFiles: boolean,

        readonly yourSteps: ReviewerStep[],
        readonly readyForYourReview: boolean,
        public isChecked : boolean = false,
        readonly isReviewer: boolean =true,
        // Set when the user sees this case only via a supervisor relationship (no
        // task assigned). yourSteps is empty in that case, so the UI uses this flag
        // to treat the user as a supervisor (e.g. to show 6-year case history).
        readonly isSupervisorRelationship: boolean = false
    ){
    }

    static fromJson(o: any): ReviewerCase {
        return new ReviewerCase(
            CaseId.fromJson(o.id),
            (<any[]> o.caseHistory).map(CaseId.fromJson),
            o.userId,
            o.separationDate,
            o.candidateName,
            Position.fromJson(o.currentPosition),
            o.proposedAction ? Action.fromJson(o.proposedAction) : (console.warn('ReviewerCase has null proposedAction:', JSON.stringify(o.id), 'status:', o.status, 'workflowType:', o.workflowType), null),
            o.proposedActionName,
            Option.fromJsonFSharp(o.periodOfReview,PeriodOfReview.fromJson),
            CaseProgress.fromJson(o.progress),
            o.canViewReviewerFiles,
            (<any[]> o.yourSteps).map(ReviewerStep.fromJson),
            o.readyForYourReview,
            false,
            o.isReviewer,
            o.isSupervisorRelationship
        );
    }

    public asCaseAdvancement() {
		return new CaseAdvancement(
			Option.create(this.proposedAction), 
			this.currentPosition);
	}
}
export class ReviewerStep {
    constructor(
        readonly title: string,
        readonly description: string,
        readonly id: string,
        readonly status: string,
        readonly requiredReviewDocument: Option<ReviewDocumentTemplate>,
        readonly readOnlyStep: boolean,
        readonly pastDue: boolean,
        readonly openDate: Date | null,
        readonly dueDate: Date | null
    ){}
    static fromJson(o: any): ReviewerStep {
        return new ReviewerStep(
            o.title,
            o.description,
            o.id,
            o.status.case,
            Option.fromJsonFSharp(o.requiredReviewDocument, ReviewDocumentTemplate.fromJson),
            o.readOnlyStep,
            !!o.pastDue,
            Option.fromJsonFSharp(o.openDate, d => new Date(d)).getValueOrDefault(null),
            Option.fromJsonFSharp(o.dueDate, d => new Date(d)).getValueOrDefault(null)
        );
    }
}
export class PeriodOfReview {
    constructor(
        readonly start: Date,
        readonly end: Date
    ){}

    static fromJson(o: any): PeriodOfReview {
        return new PeriodOfReview(new Date(o.start), new Date(o.end));
    }
}

export class ReviewFile {
    constructor(
        readonly id: string,
        readonly name: string,
        readonly committeeName: string = "",
        public isChecked: boolean = false,
        public downloadTimestamp: Date = null
    ){}
}

export class CandidateFiles {
    constructor(
        public candidateFiles: ReviewFile[],
        public candidateCanReadFiles: string[]
    ){}

    public static fromJson(files: any, fileDownloadEventArr: any): CandidateFiles {
        
        return new CandidateFiles(

            files.candidateFiles?.map((file: any) => {
                // Find matching event based on committeeName
                const fileDownloadEvent = fileDownloadEventArr.find((dt: any) => dt.committeeName === file.committeeName);

                return new ReviewFile(
                    file.id,
                    file.name,
                    file.committeeName ?? "", 
                    file.isChecked ?? false,
                    fileDownloadEvent ? new Date(fileDownloadEvent.timestamp) : null
                );
            }) || [],
            files.candidateCanReadFiles || []
        );
	}
}

export class CaseFiles {
    constructor(
        readonly candidateFiles: ReviewFile[],
        public reviewerFiles: ReviewFile[],
        readonly canReadReviewFilesFromCommitteeNames: string[]
    ){}

    public static fromJson(files: any, fileDownloadEventArr: any): CaseFiles {
        return new CaseFiles(

            files.candidateFiles?.map((file: any) => {
                // Find matching event based on committeeName
                const fileDownloadEvent = fileDownloadEventArr.find((dt: any) => dt.committeeName === file.committeeName);

                return new ReviewFile(
                    file.id,
                    file.name,
                    file.committeeName ?? "", 
                    file.isChecked ?? false,
                    fileDownloadEvent ? new Date(fileDownloadEvent.timestamp) : null
                );
            }) || [],
            files.reviewerFiles?.map((file: any) => {
                // Find matching timestamp entry based on committeeName
                const fileDownloadEvent = fileDownloadEventArr.find((dt: any) => dt.committeeName === file.committeeName);

                return new ReviewFile(
                    file.id,
                    file.name,
                    file.committeeName ?? "", 
                    file.isChecked ?? false,
                    fileDownloadEvent ? new Date(fileDownloadEvent.timestamp) : null
                );
            }) || [],
            files.canReadReviewFilesFromCommitteeNames || []
        );
	}
}
export class CaseProgress {
    //status = 'In Review';
    toString(){

        let i = this.steps.indexOf(this.activeSteps[0]);
        let n = i+1;

        if(n==0){
            
            let allStepsComplete = this.completedSteps.length > 0 && this.steps.length > 0 &&
                    this.steps.every(value => this.completedSteps.includes(value));
            
            if (allStepsComplete)
                return 'Complete';

            let waitingToBeSent = this.activeSteps.length === 0 && this.completedSteps.length === 0;

            if (waitingToBeSent)
                return 'Waiting to be sent to candidate';

            if (this.activeSteps.length === 0) {
                // Some steps complete, but no active step (next task is pending)
                let nextStep = this.steps.find(s => !this.completedSteps.includes(s));
                return nextStep ? `Pending (${nextStep})` : 'In Progress';
            }

            return this.activeSteps[0] + '*';
        }

        return `Step ${n} of ${this.steps.length} (${this.activeSteps})`;
    }

    stepActive(s): boolean 
    {
        return this.activeSteps.indexOf(s)> -1;
    }

    stepCompleted(s): boolean 
    {
        return this.completedSteps.indexOf(s)> -1;
    }

    // A step whose date gate has closed while its task is still open —
    // rendered as "Past Due" instead of "Active".
    stepPastDue(s): boolean
    {
        return this.pastDueSteps.indexOf(s) > -1;
    }

    constructor(
        readonly steps: string[],
        readonly activeSteps: string[],
        readonly completedSteps: string[],
        readonly pastDueSteps: string[] = []
    ){

    }

    static fromJson(o: any): CaseProgress {
        return new CaseProgress(
            o.steps,
            o.activeSteps,
            o.completedSteps,
            o.pastDueSteps ?? []
        );
    }
}
export class CaseDetail {
    constructor(
    ){
    }
}

// PRC & Ad Hoc committee membership (apr.CRUD_* tables — the assignments that
// drive reviewer resolution).
export interface ReviewerCommitteeMember {
    userId: number;
    name: string;
    email: string;
    writesReview: boolean;
}

// PRC is per-candidate: each candidate has their own reviewer set per year,
// with writesReview flagging the single primary writer. The list also carries
// candidates whose case this year has a PRC stage but no reviewers yet; case
// fields are null when the candidate has PRC rows but no such case.
export interface PrcCandidateCommittee {
    candidateUserId: number;
    candidateName: string;
    reviewers: ReviewerCommitteeMember[];
    caseId: number | null;
    caseDescription: string | null;
    caseStatus: string | null;
}

// A candidate on the Ad Hoc tab. Case fields are null when no incomplete case
// with an ad hoc stage backs the row — only possible for an assigned candidate,
// whose assignment outlived the case that justified it.
export interface AdHocCandidateInfo {
    userId: number;
    name: string;
    caseId: number | null;
    caseDescription: string | null;
    caseStatus: string | null;
}

// Ad Hoc committees are year-scoped rosters (chair = writesReview) with
// assigned candidates. Committees have no name — committeeId is the identity.
export interface AdHocCommitteeInfo {
    committeeId: number;
    members: ReviewerCommitteeMember[];
    candidates: AdHocCandidateInfo[];
}

// The Ad Hoc tab in one response: the committees, plus the candidates who need
// one and have none. A candidate is in exactly one of the two.
export interface AdHocView {
    committees: AdHocCommitteeInfo[];
    unassigned: AdHocCandidateInfo[];
}

// Read-only access: a grant points a user at a candidate group for a year;
// the group lists the candidates they may view.
export interface ReadOnlyCommitteeGroup {
    groupId: number;
    candidates: { userId: number; name: string }[];
    prcUsers: ReviewerCommitteeMember[];
    adHocUsers: ReviewerCommitteeMember[];
}

export abstract class AHRService {
    public abstract getCurrentPeriodOfReviewYear(): Promise<string>
    public abstract getCasesToBeReviewedCurrentUser(ahryear?: string): Promise<ReviewerCase[]>;
    public abstract getCandidateWorkflowFilesForCurrentUser(yearId: number, academicId: string): Observable<CandidateFiles>;
    public abstract getCaseFiles(caseId: CaseId, userId: number): Observable<CaseFiles>;
    public abstract dossierFileObjectUrl(yearId: number, fileId: string, academicId: string): Observable<any>;
    public abstract caseFileObjectUrl(caseId: CaseId, fileId: string): Observable<any>;
    public abstract caseFileObject(caseId: CaseId, fileId: string): Observable<any>;
    public abstract dossierFileObject(yearId: number,academicId: string, fileId: string): Observable<any>;
    public abstract downloadDossierFile(yearId: number, fileId: string, academicId: string): void;
    public abstract config(): Promise<null>;
    public abstract getCampuses(): Promise<PayrollCampus[]>;
    public abstract getSettings(): Promise<ProgramReviewSettings[]>;
    public abstract saveSettings(s: ProgramReviewSettings): Promise<Result<void,string>>;
    public abstract getReviewDateYears(): Promise<number[]>;
    public abstract getReviewDates(year: number): Promise<ReviewDatesResponse>;
    public abstract saveReviewDates(year: number, dates: ReviewDateEntry[]): Promise<Result<void,string>>;
    public abstract cloneReviewDatesFrom(sourceYear: number, targetYear: number): Promise<Result<void,string>>;
    public abstract getReviewerCommitteeYears(): Promise<number[]>;
    public abstract getPeerReviewCommittees(reviewYearId: number): Promise<PrcCandidateCommittee[]>;
    public abstract getAdHocCommittees(reviewYearId: number): Promise<AdHocView>;
    public abstract upsertPrcReviewer(reviewYearId: number, candidateUserId: number, reviewerUserId: number, writesReview: boolean): Promise<Result<void,string>>;
    public abstract removePrcReviewer(reviewYearId: number, candidateUserId: number, reviewerUserId: number): Promise<Result<void,string>>;
    public abstract createAdHocCommittee(reviewYearId: number): Promise<number>;
    public abstract deleteAdHocCommittee(reviewYearId: number, committeeId: number): Promise<Result<void,string>>;
    public abstract upsertAdHocMember(reviewYearId: number, committeeId: number, userId: number, writesReview: boolean): Promise<Result<void,string>>;
    public abstract removeAdHocMember(reviewYearId: number, committeeId: number, userId: number): Promise<Result<void,string>>;
    public abstract assignAdHocCandidate(reviewYearId: number, committeeId: number, candidateUserId: number): Promise<Result<void,string>>;
    public abstract unassignAdHocCandidate(reviewYearId: number, committeeId: number, candidateUserId: number): Promise<Result<void,string>>;
    public abstract getReadOnlyCommitteeGroups(reviewYearId: number): Promise<ReadOnlyCommitteeGroup[]>;
    public abstract addReadOnlyReviewer(reviewYearId: number, kind: 'prc'|'adhoc', userId: number, groupId?: number): Promise<Result<void,string>>;
    public abstract removeReadOnlyReviewer(reviewYearId: number, kind: 'prc'|'adhoc', userId: number): Promise<Result<void,string>>;
    public abstract addReadOnlyCandidate(groupId: number, candidateUserId: number): Promise<Result<void,string>>;
    public abstract removeReadOnlyCandidate(groupId: number, candidateUserId: number): Promise<Result<void,string>>;
    public abstract getCaseReviewDateOverrides(caseId: number): Promise<CaseReviewDateOverridesResponse>;
    public abstract saveCaseReviewDateOverrides(caseId: number, dates: ReviewDateEntry[]): Promise<Result<void,string>>;
    public abstract getSystemClock(): Promise<SystemClockInfo>;
    public abstract setSystemClock(date: string): Promise<Result<void,string>>;
    public abstract clearSystemClock(): Promise<void>;
    public abstract appealResult(caseId: string): Promise<Result<null,string>>;
    public abstract getAllAcademics(): Promise<HRAcademic[]>;
    public abstract getAllTitlesRanksAndSteps(): Title[];
    public abstract execute(cmd: HRCommand): Promise<Result<null,string>>;
    public abstract createNewAcademic(userId: number, startingPositionId: PurePositionId, term: Option<TermId>, startDate: Date): Promise<Result<null,string>>;
    public abstract getAllANRUnits(): Promise<HRUnit[]>;
    public abstract get cases(): Observable<Case[]>;
    public abstract getCaseById(caseId: string): Promise<Option<Case>>;
    public abstract getCasesForCurrentUser(): Observable<Case[]>;
    public abstract getCasesForCurrentUserByYear(yearId: number): Observable<Case[]>;
    public abstract getSupervisorsForCurrentUser(): Observable<Supervisor[]>;
    public abstract getNewsStoriesForCurrentUser(yearId: number): Observable<NewsStory[]>;
    public abstract proposeAction(caseId: string, action: ActionProposal): Promise<Result<void,string>>;
    public abstract sendToCandidate(caseId: string): Promise<Result<void,string>>;
    public abstract recallFromCandidate(caseId: string, reason: string): Promise<Result<void,string>>;
    public abstract proposeDeferral(caseId: string, reason: string): Promise<Result<void,string>>;
    public abstract approveDeferral(caseId: string): Promise<Result<void,string>>;
    public abstract proposeTermReview(caseId: string): Promise<Result<void,string>>;
    public abstract get newsStories(): Observable<NewsStory[]>;
    public abstract newsStoriesByCaseId(caseId: string): Observable<NewsStory[]>;
    public abstract promoteSupervisor(id: string, userId: number): Promise<Result<null,string>>;
    public abstract demoteSupervisor (id: string, userId: number): Promise<Result<null,string>>;
    public abstract addSupervisor (id: string, userId: number, reviewParticipationId: string): Promise<Result<null,string>>;
    public abstract removeSupervisor (id: string, userId: number): Promise<Result<null,string>>;
    public abstract denyProposedAdvancement(caseId: string, secondaryAction: any, actionType: ActionType): Promise<Result<null,string>>;
    public abstract approveProposedAdvancement(caseId: string, yearId: number, action: ActionType): Promise<Result<null,string>>;

    public abstract getProgressionTable(facts: PositionFacts): Promise<Option<ProgressionTable>>;
    public abstract getReviewRequirements(titleId: number, rankId: number, stepId: number): Promise<ComprehensiveReviewRequirements>;

    public abstract subscribeToNotifications(): void;
    public abstract unsubscribeToNotifications(): void;
    public abstract removeCountyAssignment(caseId: string, countyId: number): Promise<Result<null,string>>;
    public abstract addCountyAssignment(caseId: string, countyId: number): Promise<Result<null,string>>;

    public abstract downloadAcademicExcel(): void;
    public abstract downloadCaseReportExcel(): void;
    public abstract downloadFileDownloadEventExcel(): void;
    public abstract downloadEligibilityListExcel(rows: EligibilityListReportRow[]): void;

    public abstract getWorkflowTaskIdForReviewer(workflowCaseId: number): Promise<number | null>;
    public abstract getCaseToBeReviewedByWorkflowCaseId(workflowCaseId: number): Promise<Option<ReviewerCase>>;
    public abstract getDirectReportsForCurrentUser(): Observable<SupervisorDirectReports[]>
    public abstract updateUserSeparationDate(c: Case): Promise<Result<void, string>>
    public abstract getCaseReconciliation(caseId: number): Promise<Result<CaseReconciliation, string>>;
    public abstract applyReconciliation(caseId: number, changes: ReconChange[]): Promise<Result<CaseReconciliation, string>>;
}

export class ActionProposal {
    constructor(
        public readonly type: ActionProposalType,
        public readonly advancement: Advancement
    ){ }

    public toJsonFsharp(): any {
        return this.type == ActionProposalType.AdvancementTo
            ? {
                case: this.type,
                fields: [ this.advancement.toJsonFsharp() ]
            } : {
                case: this.type
            };
    }
}

export class Advancement {
    constructor(
        public readonly position: TitleRankAndStepId,
        public readonly term: Option<TermId>
    ){}
    public toJsonFsharp(): any {
        return {
            positionId: this.position,
            term: this.term.toJsonFSharp(t => ({case:t}))
        };
    }
}

export enum ActionProposalType{
     AnnualEvaluation = "AnnualEvaluation"
    ,Goals            = "Goals"
    ,AdvancementTo    = "AdvancementTo"
    ,TermAdvancement  = "TermAdvancement"
    ,Deferral         = "Deferral"
    ,FiveYearReview   = "FiveYearReview"
}

export class ProgressionTable {
    constructor(
        public readonly id: number,
        public readonly name: string,
        public readonly ranks: ProgressionTableRank[],
        public readonly overlappingSteps: OverlappingStep[]
    ){ }

    public static fromJson(o: any): ProgressionTable {
        return new ProgressionTable(
            o.id,
            o.name,
            (<any[]>o.ranks).map(ProgressionTableRank.fromJson),
            (<any[]>o.overlappingSteps).map(OverlappingStep.fromJson)
        );
    }

    public lastRank(): ProgressionTableRank {
        return this.ranks[this.ranks.length-1];
    }

    public lastStep(): ProgressionTableStep {
        return this.lastRank().steps[this.lastRank().steps.length-1];
    }
}

export class ProgressionTableRank {
    constructor(
        public readonly id: number,
        public readonly name: string,
        public readonly steps: ProgressionTableStep[]
    ){ }

    public static fromJson(o: any): ProgressionTableRank {
        return new ProgressionTableRank(
            o.id,
            o.name,
            (<any[]>o.steps).map(ProgressionTableStep.fromJson)
        );
    }
}

export class OverlappingStep {
    constructor(
        public readonly a: RankAndStepId,
        public readonly b: RankAndStepId
    ){}
    public static fromJson(o: any): OverlappingStep{
        return new OverlappingStep(
            RankAndStepId.fromJson(o.a),
            RankAndStepId.fromJson(o.b));
    }
}

export class RankAndStep {
    constructor(
        public readonly rank: string,
        public readonly step: number){}
    public static fromJson(o: any): RankAndStep {
        return new RankAndStep(o.rank, o.step);
    }
}

export class TitleRankAndStepId {
    constructor(
        public readonly titleId: number,
        public readonly rankId: number,
        public readonly stepId: number){}
    public static fromJson(o: any): TitleRankAndStepId {
        return new TitleRankAndStepId(o.titleId, o.rankId, o.stepId);
    }
}

export class TitleRankStepAndTermId {
    constructor(
        public readonly titleId: number,
        public readonly rankId: number,
        public readonly stepId: number,
        public readonly termId: TermId){}
    public static fromJson(o: any): TitleRankStepAndTermId {
        return new TitleRankStepAndTermId(o.titleId, o.rankId, o.stepId, o.termId);
    }
    public toTitleRanksAndStepId() {
        return new TitleRankAndStepId(this.titleId, this.rankId, this.stepId);
    }
}

export class RankAndStepId {
    constructor(
        public readonly rankId: number,
        public readonly stepId: number){}
    public static fromJson(o: any): RankAndStepId {
        return new RankAndStepId(o.rankId, o.stepId);
    }
}

export class ProgressionTableStepRelationship {
    constructor(
        public readonly withRankAndStep: RankAndStep,
        public readonly type: string
    ){}
    public static fromJson(o: any): ProgressionTableStepRelationship {
        return new ProgressionTableStepRelationship(
            RankAndStep.fromJson(o.withRankAndStep),
            ProgressionTableStepRelationshipType.fromJson(o.type)
        );
    }
}

class ProgressionTableStepRelationshipType 
{
    public static fromJson(o: any): string {
        switch(o.case){
            case "Overlaps":        return "overlaps";
            case "IsOverlappedBy":  return "is overlapped by";
            case "PromotesTo":      return "promotes to";
            case "PromotesFrom":    return "promotes from";
            default: return o;
        }
    }
}

export class ProgressionTableStep {
    constructor(
        public readonly id: number,
        public readonly determination: any,
        public readonly relationships: ProgressionTableStepRelationship[],
        public readonly durationInMonths: number
    ){}
    public static fromJson(o: any): ProgressionTableStep {
        return new ProgressionTableStep(
            o.id,
            ProgressionTableStep.determinationFromJson(o.determination),
            (<any[]>o.relationships).map(ProgressionTableStepRelationship.fromJson),
            Option.fromJsonFSharp(o.durationInMonths, j => j).getValueOrDefault(null)
        );
    }

    private static determinationFromJson(o: any): any {
        switch(o.case){
            case 'CurrentStep': return 'Current Step';
            case 'Regression':  return 'Regression';
            case 'Unreachable': return 'Unreachable';
            case 'Advancement': 
                let advancementType = o.fields[0].type.case;
                let advancementSituation = '';
                Option
                .fromJsonFSharp(o.fields[0].exceptionalTiming, o => o)
                .doWithValue(timing => {
                    switch(timing.case){
                        case 'MonthRule':
                            advancementSituation = `${timing.fields[0]}mo`;
                            break;
                        case 'Acceleration':
                            advancementSituation = 'Acc.'
                            break;
                    }
                });
                let eligible = o.fields[0].eligible ? '(E)' : '';
                return (advancementSituation + ' ' + advancementType + ' ' + eligible).trim();
        }
    }
}

export class PositionFacts {
    constructor(
        public readonly titleId: number,
        public readonly rankId: number,
        public readonly stepId: number,
        public readonly monthsServedInCurrentTitle: number,
        public readonly monthsServedInCurrentRank: number,
        public readonly monthsServedInCurrentStep: number
    ){ }
}
